import React from 'react';
import { Icon } from '../icons';
import { X } from '../icons';

interface PhaseProgressPopupProps {
  selectedEvent: {
    id: string;
    title: string;
    description?: string;
    start: string;
    end: string;
    participants?: string[];
    phase: string;
    protocol?: Record<string, { completed?: boolean; scheduled?: boolean }>;
    type?: string;
  };
  phases: Record<string, { name: string; icon: string; color: string; duration: number | null }>;
  onClose: () => void;
  onSchedulePhase?: (phaseKey: string) => void;
  onSkipPhase?: (phaseKey: string) => void;
  onSyncReschedule?: () => void;
  onManualReschedule?: () => void;
  onModify?: () => void;
}

const PhaseProgressPopup: React.FC<PhaseProgressPopupProps> = ({
  selectedEvent,
  phases,
  onClose,
  onSchedulePhase,
  onSkipPhase,
  onSyncReschedule,
  onManualReschedule,
  onModify,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-full max-w-lg mx-4">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-800">ConnectFlow Protocol</h3>
              <p className="text-sm text-gray-500">{selectedEvent.title}</p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Icon icon={X} size={16} strokeWidth={1.5} color="#9ca3af" />
            </button>
          </div>
        </div>

        <div className="p-5">
          <div className="mb-6">
            <h4 className="font-medium text-gray-800 mb-3">Voortgang Protocol</h4>
            <div className="space-y-3">
              {Object.entries(phases).slice(1, -1).map(([key, phase]) => {
                const protocolPhase = selectedEvent.protocol?.[key];
                const isCompleted = protocolPhase?.completed;
                const isScheduled = protocolPhase?.scheduled;
                
                return (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isCompleted ? 'bg-green-100 text-green-600' :
                        isScheduled ? 'bg-orange-100 text-orange-600' :
                        'bg-gray-100 text-gray-400'
                      }`}>
                        {isCompleted ? '✓' : phase.icon}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{phase.name}</p>
                        <p className="text-xs text-gray-500">
                          {isCompleted ? 'Voltooid' : 
                           isScheduled ? 'Gepland' : 
                           'In afwachting'}
                        </p>
                      </div>
                    </div>
                    
                    {!isCompleted && (
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => onSkipPhase?.(key)}
                          className="text-xs bg-gray-200 text-gray-600 px-3 py-1 rounded hover:bg-gray-300 transition-colors"
                        >
                          Overslaan
                        </button>
                        <button 
                          onClick={() => onSchedulePhase?.(key)}
                          className="text-xs bg-orange-500 text-white px-3 py-1 rounded hover:bg-orange-600 transition-colors"
                        >
                          Plannen
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4 mb-4">
            <h5 className="font-medium text-orange-800 mb-2">Huidige Fase: {phases[selectedEvent.phase as keyof typeof phases]?.name}</h5>
            <p className="text-sm text-orange-700 mb-3">{selectedEvent.description}</p>
            <div className="text-sm text-orange-600 space-y-1">
              <div>📅 {selectedEvent.start} - {selectedEvent.end}</div>
              <div>👥 {selectedEvent.participants?.join(', ')}</div>
              <div className="text-xs">🔄 Gesynchroniseerd met beide agenda's</div>
            </div>
          </div>

          {/* Tijd Aanpassing */}
          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <h5 className="font-medium text-blue-800 mb-3">Tijd Aanpassen</h5>
            <div className="space-y-2">
              <button
                onClick={() => {
                  onSyncReschedule?.();
                  onClose();
                }}
                className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium flex items-center justify-center space-x-2"
              >
                <span>🔄</span>
                <span>Nieuwe Tijd Voorstellen (Sync A+B)</span>
              </button>
              
              <button
                onClick={() => {
                  onManualReschedule?.();
                  onClose();
                }}
                className="w-full bg-blue-400 text-white py-2 rounded-lg hover:bg-blue-500 transition-colors text-sm font-medium flex items-center justify-center space-x-2"
              >
                <span>⏰</span>
                <span>Eigen Tijd Voorstellen</span>
              </button>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              💡 Sync zoekt automatisch beschikbare momenten voor beide personen
            </p>
          </div>

          {/* Acties */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => {
                onModify?.();
                onClose();
              }}
              className="bg-orange-500 text-white py-2.5 rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
            >
              Wijzigen
            </button>
            <button
              onClick={onClose}
              className="bg-gray-100 text-gray-600 py-2.5 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Sluiten
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhaseProgressPopup;






